(() => {
  if (customElements.get('biz-menu-offer')) return;
  const engine = window.BizSpotlightEngine;
  const cache = new Map();
  const pending = new Map();
  const cleanHandle = value => /^[a-z0-9][a-z0-9-]*$/.test(value || '');
  async function getPool(handle, force = false) {
    const saved = cache.get(handle);
    if (!force && saved && Date.now() - saved.at < 300000) return saved.data;
    if (pending.has(handle)) return pending.get(handle);
    const request = (async () => {
      const url = new URL('/collections/' + handle, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('section_id', 'bizedeals-menu-promo');
      const response = await fetch(url, { cache:'no-store', signal:AbortSignal.timeout(12000) });
      if (!response.ok) throw Error('Menu offer unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = JSON.parse(doc.querySelector('[data-bd-menu-payload]')?.textContent || '{}');
      if (data.handle !== handle || !Array.isArray(data.items)) throw Error('Mismatched menu collection');
      cache.set(handle, { data, at:Date.now() });
      return data;
    })();
    pending.set(handle, request);
    try { return await request; } finally { pending.delete(handle); }
  }
  class CollectionFeature extends HTMLElement {
    connectedCallback() {
      if (this.ready || !engine) return;
      this.ready = true;
      const data = JSON.parse(this.querySelector('[data-feature-payload]').textContent);
      this.items = data.items; this.labels = data.labels; this.blocked = new Set(); this.excluded = new Set((data.exclude || []).map(String));
      this.handle = this.dataset.source; this.loaded = Date.now();
      this.cards = [...this.querySelectorAll('[data-feature-card]')];
      this.selected = []; this.bucket = null;
      this.onVisibility = () => this.sync();
      this.onImageError = event => {
        const card = event.target.closest('[data-feature-card]');
        if (card?.dataset.product) { this.blocked.add(card.dataset.product); this.pick(true); }
      };
      this.addEventListener('error', this.onImageError, true);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting; this.sync();
      }, {rootMargin:'150px'});
      this.observer.observe(this.parentElement);
      this.pick();
    }
    disconnectedCallback() {
      this.observer?.disconnect(); clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.removeEventListener('error', this.onImageError, true); this.ready = false;
    }
    config() {
      return {allowStaleProduct:true,requireDiscount:true,collectionHandle:this.handle,rule:this.hasAttribute('data-edeals-only') ? 'edeal' : this.handle === 'amazon-best-sellers' || this.hasAttribute('data-best-only') ? 'bestseller' : 'any',allowPrime:true,allowFallback:false,rotateHours:6,slot:'feature-'+this.handle};
    }
    sync() {
      clearInterval(this.timer);
      if (!this.visible || document.hidden || !this.isConnected) return;
      const tick = () => {
        if (document.hidden || !this.visible) return;
        this.pick();
        if (Date.now()-this.loaded >= 300000) this.refresh();
      };
      tick(); this.timer = setInterval(tick,15000);
    }
    async refresh() {
      if (this.loading || !cleanHandle(this.handle)) return;
      this.loading = true;
      try {
        const data = await getPool(this.handle,true);
        if (!this.isConnected) return;
        this.items = data.items; this.loaded = Date.now(); this.pick();
      } catch { this.loaded = Date.now(); this.pick(); }
      finally { this.loading = false; }
    }
    pick(force = false) {
      const now = Date.now(), config = this.config(), bucket = Math.floor(now/21600000);
      const used = new Set([...this.blocked, ...this.excluded]);
      if (this.hasAttribute('data-discovery-rail')) {
        for (const rail of this.closest('.bd-collection-discovery').querySelectorAll('[data-discovery-rail]')) {
          if (rail === this) break;
          rail.querySelectorAll('[data-product]:not([hidden])').forEach(card => used.add(card.dataset.product));
        }
      }
      const next = this.cards.map((card,index) => {
        const previous = this.items.find(item => item && item.id === this.selected[index]);
        let item = !force && this.bucket === bucket && !used.has(previous?.id) ? engine.eligible(previous,config,now) : null;
        if (!item) item = engine.choose(this.items,config,now,used,'feature');
        if (item) used.add(item.id);
        return item;
      });
      this.bucket = bucket; this.selected = next.map(item=>item?.id);
      next.forEach((item,index)=>this.renderCard(this.cards[index],item));
      this.toggleAttribute('data-has-products',next.some(Boolean));
      this.dispatchEvent(new CustomEvent('bd:rail-updated', {bubbles:true}));
    }
    renderCard(card,item) {
      const q=s=>card.querySelector(s);
      if (!item) {
        card.hidden=true; delete card.dataset.product;
        q('[data-feature-image]').removeAttribute('src');
        card.querySelectorAll('[data-feature-link]').forEach(link=>link.removeAttribute('href'));
        return;
      }
      const sale = item.reference > item.price && item.displayDiscount > 0;
      const edeal = q('[data-feature-edeal]'); if(edeal) edeal.hidden = !(sale && item.displayDiscount >= 20);
      const money=cents=>new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(cents/100);
      card.dataset.product=item.id;
      card.querySelectorAll('[data-feature-link]').forEach(link=>link.href=item.url);
      q('[data-feature-title]').textContent=item.title;
      q('[data-feature-image]').alt=item.title;
      if(q('[data-feature-image]').getAttribute('src')!==item.image)q('[data-feature-image]').src=item.image;
      q('[data-feature-discount]').textContent=this.labels.off.replace('[percent]',item.displayDiscount);
      q('[data-feature-discount]').hidden=!sale;
      q('[data-feature-condition]').textContent=item.prime?this.labels.prime:item.limited?this.labels.limited:'';
      q('[data-feature-price]').textContent=item.pricePending?'Check current price on Amazon':(sale?this.labels.now+' ':'')+money(item.price);
      q('[data-feature-price]').toggleAttribute('data-sale',sale);
      q('[data-feature-reference]').textContent=sale?money(item.reference):'';
      q('[data-feature-reference]').hidden=!sale;
      q('[data-feature-rank]').textContent=item.rank?this.labels.rank.replace('[rank]',item.rank.rank).replace('[category]',item.rank.name):'';
      q('[data-feature-rank]').hidden=this.handle!=='amazon-best-sellers'||!item.rank;
      q('[data-feature-updated]').textContent=this.labels.updated+': '+new Date(item.snapshot.fetchedAt).toLocaleString(undefined,{dateStyle:'medium',timeStyle:'short'});
      q('.bd-feature-info').hidden=Boolean(item.pricePending);
      card.hidden=false;
      card.dispatchEvent(new CustomEvent('bz:promo-ready',{bubbles:true}));
    }
  }
  customElements.define('biz-collection-feature',CollectionFeature);

  class MenuOffer extends HTMLElement {
    connectedCallback() {
      if (this.ready || !engine) return;
      this.ready = true;
      this.menu = this.closest('.bd-smart-menu');
      this.labels = JSON.parse(this.querySelector('[data-menu-labels]').textContent);
      this.turn = 0;
      this.blocked = new Set();
      this.onToggle = () => this.sync();
      this.onChoice = event => {
        const link = event.target.closest('[data-menu-source]');
        if (!link || !this.menu.contains(link)) return;
        clearTimeout(this.choiceTimer);
        this.choiceTimer = setTimeout(() => this.select(link), event.type === 'focusin' ? 0 : 150);
      };
      this.onNext = () => { this.turn++; this.pick(); };
      this.onError = () => { if (this.item) this.blocked.add(this.item.id); this.pick(); };
      this.onVisible = () => { if (!document.hidden) { this.check(); this.sync(); } };
      this.menu.addEventListener('toggle', this.onToggle);
      this.menu.addEventListener('pointerover', this.onChoice);
      this.menu.addEventListener('focusin', this.onChoice);
      this.querySelector('[data-menu-next]').addEventListener('click', this.onNext);
      this.querySelector('[data-menu-image]').addEventListener('error', this.onError);
      document.addEventListener('visibilitychange', this.onVisible);
      this.sync();
    }
    disconnectedCallback() {
      this.menu?.removeEventListener('toggle', this.onToggle);
      this.menu?.removeEventListener('pointerover', this.onChoice);
      this.menu?.removeEventListener('focusin', this.onChoice);
      this.querySelector('[data-menu-next]')?.removeEventListener('click', this.onNext);
      this.querySelector('[data-menu-image]')?.removeEventListener('error', this.onError);
      document.removeEventListener('visibilitychange', this.onVisible);
      clearInterval(this.timer); clearTimeout(this.choiceTimer);
      this.ready = false;
    }
    isActive() { return this.isConnected && this.menu.open && !document.hidden && this.getClientRects().length > 0; }
    sync() {
      clearInterval(this.timer);
      if (!this.isActive()) return;
      if (!this.handle) {
        const choices = [...this.menu.querySelectorAll('[data-menu-source]')];
        const context = location.pathname.split('/collections/')[1]?.split('/')[0];
        this.select(choices.find(link => link.dataset.menuSource === context) || choices[0]);
      } else if (!this.loaded || Date.now() - this.loaded >= 300000) this.load(true);
      else this.check();
      this.timer = setInterval(() => {
        if (!this.isActive()) return;
        this.check();
        if (!this.loaded || Date.now() - this.loaded >= 300000) this.load(true);
      },15000);
    }
    select(link) {
      if (!link || !this.isActive() || !cleanHandle(link.dataset.menuSource)) return;
      const handle = link.dataset.menuSource;
      if (this.handle === handle) return;
      this.handle = handle; this.turn = 0; this.blocked.clear(); this.data = null; this.loaded = 0;
      this.menu.querySelectorAll('[data-current]').forEach(node => node.removeAttribute('data-current'));
      link.setAttribute('data-current','');
      this.querySelector('[data-menu-heading]').textContent = link.dataset.menuTitle;
      this.querySelector('[data-menu-browse]').href = '/collections/' + handle;
      this.hide(this.dataset.menuLoading);
      this.load();
    }
    async load(force = false) {
      const handle = this.handle;
      if (!cleanHandle(handle) || this.loading === handle || !this.isActive()) return;
      this.loading = handle;
      try {
        const data = await getPool(handle,force);
        if (this.handle !== handle || !this.isConnected) return;
        this.data = data; this.loaded = Date.now(); this.pick();
      } catch {
        if (this.handle === handle) { this.loaded = Date.now(); this.hide(); }
      } finally { if (this.loading === handle) this.loading = null; }
    }
    config() { return {allowStaleProduct:true,requireDiscount:true,collectionHandle:this.handle,rule:this.handle === 'e-deals' ? 'edeal' : 'any',allowPrime:true,allowFallback:false,rotateHours:6,slot:'menu-'+this.handle}; }
    hide(message) {
      this.item = null;
      this.querySelector('[data-menu-card]').hidden = true;
      this.querySelector('[data-menu-state]').hidden = false;
      this.querySelector('[data-menu-state]').textContent = message || this.labels.empty;
      this.querySelector('[data-menu-next]').hidden = true;
      this.querySelector('[data-menu-image]').removeAttribute('src');
      this.querySelectorAll('[data-menu-product]').forEach(link => link.removeAttribute('href'));
    }
    check() {
      if (this.item && engine.eligible(this.item,this.config(),Date.now()) && !this.item.pricePending) return;
      this.pick();
    }
    pick() {
      if (!this.data || !this.isActive()) return;
      const item = engine.choose(this.data.items,this.config(),Date.now(),this.blocked,'menu',this.turn);
      if (!item) { this.hide(); return; }
      this.item = item;
      const q = selector => this.querySelector(selector);
      const money = cents => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(cents/100);
      const sale = item.reference > item.price && item.displayDiscount > 0;
      this.querySelectorAll('[data-menu-product]').forEach(link => { link.href = item.url; link.setAttribute('aria-label',item.title); });
      q('[data-menu-title]').textContent = item.title;
      q('[data-menu-vendor]').textContent = ['Amazon',item.brand].filter(Boolean).join(' · ');
      q('[data-menu-discount]').textContent = this.labels.off.replace('[percent]',item.displayDiscount);
      q('[data-menu-discount]').hidden = !sale;
      q('[data-menu-condition]').textContent = item.pricePending ? '' : item.prime ? this.labels.prime : item.snapshot.offerType === 'LIGHTNING_DEAL' ? this.labels.lightning : item.limited ? this.labels.limited : item.snapshot.deal ? this.labels.deal : '';
      q('[data-menu-price]').textContent = item.pricePending ? 'Check current price on Amazon' : (sale ? this.labels.now + ' ' : '') + money(item.price);
      q('.bd-menu-offer__price').toggleAttribute('data-sale',sale);
      q('[data-menu-old]').textContent = sale ? money(item.reference) : '';
      q('[data-menu-old]').hidden = !sale;
      q('[data-menu-saving]').hidden = !sale;
      q('[data-menu-save]').textContent = sale ? money(item.reference-item.price) : '';
      q('[data-menu-rank]').textContent = item.rank ? this.labels.rank.replace('[rank]',item.rank.rank).replace('[category]',item.rank.name) : '';
      q('[data-menu-rank]').hidden = !item.rank;
      q('[data-menu-updated]').textContent = this.labels.updated + ': ' + new Date(item.snapshot.fetchedAt).toLocaleString(undefined,{dateStyle:'medium',timeStyle:'short'});
      q('[data-menu-updated]').hidden=Boolean(item.pricePending);
      const img=q('[data-menu-image]'); img.alt=item.title; if(img.getAttribute('src')!==item.image) img.src=item.image;
      q('[data-menu-card]').hidden=false; q('[data-menu-state]').hidden=true;
      q('[data-menu-next]').hidden=engine.pool(this.data.items,this.config(),Date.now(),this.blocked).length<2;
    }
  }
  customElements.define('biz-menu-offer',MenuOffer);
  document.addEventListener('toggle',event=>{
    if (!event.target.matches('.bd-smart-menu[open],.bd-mega-menu[open]')) return;
    document.querySelectorAll('.bd-smart-menu[open],.bd-mega-menu[open]').forEach(menu=>{if(menu!==event.target)menu.open=false;});
  },true);
  document.addEventListener('click',event=>{
    document.querySelectorAll('.bd-smart-menu[open],.bd-mega-menu[open]').forEach(menu=>{if(!menu.contains(event.target))menu.open=false;});
  });
  document.addEventListener('keydown',event=>{
    if(event.key!=='Escape')return;
    const menu=[...document.querySelectorAll('.bd-smart-menu[open],.bd-mega-menu[open]')].find(node=>node.getClientRects().length);
    if(menu){menu.open=false;menu.querySelector('summary').focus();}
  });
})();

